import React, { createContext, useContext, useEffect, useState } from 'react';
import { GoogleMapCoordinatesData, LatLongData } from '../services/api/types';
import { useApi } from '../hooks/useApi';
import { useAuthentication } from './useAuthentication';

export interface GoogleMapCoordinatesContextProps {
  googleMapCoordinatesData: GoogleMapCoordinatesData;
  setGoogleMapCoordinatesData: React.Dispatch<
    React.SetStateAction<GoogleMapCoordinatesData>
  >;
}

export const GoogleMapCoordinatesContext =
  createContext<GoogleMapCoordinatesContextProps | null>(null);

export const GoogleMapCoordinatesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { api } = useApi();
  const [loading, setLoading] = useState(true);
  const { auth } = useAuthentication();

  const isUserSignedIn = auth.isUserSignedIn();

  const [googleMapCoordinatesData, setGoogleMapCoordinatesData] =
    useState<GoogleMapCoordinatesData>({
      is_google_map_coordinates: false,
      google_map_coordinates: [],
    });

  useEffect(() => {
    let isMounted = true;
    let isCoordinates = false;
    let coordinatesList: LatLongData[];

    const getGoogleMapCoordinates = async () => {
      if (isUserSignedIn) {
        const googleMapCoordinatesResponse =
          await api.get_google_map_coordinates();
        if (
          googleMapCoordinatesResponse &&
          googleMapCoordinatesResponse.is_google_map_coordinates
        ) {
          isCoordinates =
            googleMapCoordinatesResponse.is_google_map_coordinates;
          coordinatesList = googleMapCoordinatesResponse.google_map_coordinates;
        }
      }

      if (isMounted) {
        setLoading(false);
        setGoogleMapCoordinatesData({
          is_google_map_coordinates: isCoordinates,
          google_map_coordinates: coordinatesList,
        });
      }
    };

    if (!isUserSignedIn) {
      setLoading(false);
    } else {
      if (!googleMapCoordinatesData.is_google_map_coordinates) {
        getGoogleMapCoordinates();
      } else {
        setLoading(false);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [
    api,
    googleMapCoordinatesData.google_map_coordinates,
    googleMapCoordinatesData.is_google_map_coordinates,
  ]);

  if (loading) {
    return null;
  }

  return (
    <GoogleMapCoordinatesContext.Provider
      value={{
        googleMapCoordinatesData,
        setGoogleMapCoordinatesData,
      }}
    >
      {children}
    </GoogleMapCoordinatesContext.Provider>
  );
};

export function useGoogleMapCoordinates(): GoogleMapCoordinatesContextProps {
  const context = useContext(GoogleMapCoordinatesContext);
  return context || assertNever();
}

function assertNever(): never {
  throw Error(
    'Cannot use useGoogleMapCoordinates outside of GoogleMapCoordinatesProvider',
  );
}
